import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { AuthHeader } from 'components';
import Join from 'components/Auth/Join';
import { useGoogleLogin } from 'hooks/useGoogleLogin';
import Utils from 'lib/Utils';
import routeConstants from 'routes/routeConstants';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Invites, verifyInviteRequest } from 'store/Invites';
import { clearSignUpUser } from 'store/SignUp';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, InviteParamsUrl } from 'types';
import AuthTerms from './AuthTerms';

const AuthAcceptInviteCreateAccount: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { inviteToken } = useParams<InviteParamsUrl>();
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Invites.VERIFY_INVITE_REQUEST)) as IApiErrorPayloadWithCode;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Invites.VERIFY_INVITE_REQUEST));

  useEffect(() => {
    // Clear signup
    dispatch(clearSignUpUser());

    // Verify the invite token is valid
    dispatch(verifyInviteRequest(inviteToken));
  }, [inviteToken]);

  const onEmailSignUpClick = () => {
    history.push(routeConstants.public.signUpEmail);
  };

  const handleGoogleLogin = useGoogleLogin({
    redirectUri: Utils.buildUrl(routeConstants.public.redirectGoogle.signUp),
    state: inviteToken,
  });

  useEffect(() => {
    if (apiErrors?.statusCode) {
      switch (apiErrors.statusCode) {
        case '404':
          history.push('/invites/not-found');
          break;
        case '406':
          history.push('/invites/expired');
          break;
        case '410':
          history.push('/invites/already-claimed');
          break;
        case '422':
          history.push('/invites/invalid');
          break;
        default:
          break;
      }
    }
  }, [history, apiErrors]);

  return (
    <Join
      data-cy="accept-invite-join-landing"
      loginLink={routeConstants.public.invites.login}
      onEmailSignUpClick={onEmailSignUpClick}
      handleGoogleLogin={handleGoogleLogin}
      authHeader={<AuthHeader data-cy="accept-invitation-header" title="Accept Invitation" subtitle="You’ve been invited to join your employer on Kickfin. Create an account or log in to accept the invitation." />}
      authTerms={<AuthTerms />}
      isLoading={isLoading !== false}
    />
  );
};

export default AuthAcceptInviteCreateAccount;
